import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import ConnectorEdit from '../components/ConnectorSettings/ConnectorEdit';
import {
  updateConnector,
  testConnectorCredentials,
  fetchConnector,
} from 'js/actions/connector-actions';
import { queryIssueFieldsForConnector } from 'js/actions/jira-connector-actions';
import { notify } from 'js/actions/alert-actions';
import { selectActiveWorkspace } from 'js/store/reducers/workspaces';
import { selectConnectorById } from 'js/store/selectors/connectors';

function ConnectorEditContainer({
  updateConnector,
  testConnectorCredentials,
  fetchConnector,
  queryIssueFieldsForConnector,
  notify,
  activeWorkspace,
  connector,
  history,
  match,
}) {
  const [isConnectorLoading, setIsConnectorLoading] = useState(false);
  const [issueFields, setIssueFields] = useState([]);

  const workspaceId = activeWorkspace?.id;
  const connectorId = match.params.connectorId;

  // Fetch connector if not available
  useEffect(() => {
    if (workspaceId && connectorId && !connector && !isConnectorLoading) {
      setIsConnectorLoading(true);
      fetchConnector(workspaceId, connectorId)
        .catch((error) => {
          console.error('Failed to fetch connector:', error);
          notify({
            type: 'danger',
            message: 'Failed to load connector. Please try again.',
          });
          // Navigate back to connectors list
          history.push(match.url.replace(`/${connectorId}/edit`, ''));
        })
        .finally(() => {
          setIsConnectorLoading(false);
        });
    }
  }, [
    workspaceId,
    connectorId,
    connector,
    isConnectorLoading,
    fetchConnector,
    notify,
    history,
    match.url,
  ]);

  // Load Jira issue fields when starting edit so the full list is available for changing mapping
  useEffect(() => {
    if (connector?.id && workspaceId && connector.type?.startsWith('jira')) {
      queryIssueFieldsForConnector(connector?.id, {})
        .then((fields) => setIssueFields(Array.isArray(fields) ? fields : []))
        .catch(() => setIssueFields([]));
    }
  }, [
    connector?.id,
    connector?.type,
    workspaceId,
    queryIssueFieldsForConnector,
  ]);

  const handleSubmit = useCallback(
    async (formValues) => {
      try {
        // For updates, only include encrypted_credentials if a new token is provided
        const updateData = { ...formValues };
        if (
          !formValues.encrypted_credentials?.token ||
          formValues.encrypted_credentials.token.trim() === ''
        ) {
          delete updateData.encrypted_credentials;
        }

        await updateConnector(workspaceId, connectorId, updateData);
        notify({
          type: 'success',
          message: 'Connector updated successfully',
        });
        history.push(match.url.replace(`/${connectorId}/edit`, ''));
      } catch (error) {
        notify({
          type: 'danger',
          message:
            'Failed to update connector. Please check your configuration.',
        });
      }
    },
    [workspaceId, connectorId, updateConnector, notify, history, match.url]
  );

  const handleCancel = () => {
    history.push(match.url.replace(`/${connectorId}/edit`, ''));
  };

  const handleTestConnector = useCallback(
    async (testData) => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      // Edit mode: send form payload + connector_id so server merges with stored connector
      // and uses stored credentials when token is blank
      return testConnectorCredentials(workspaceId, testData, connectorId);
    },
    [workspaceId, connectorId, testConnectorCredentials]
  );

  const handleQueryIssueFields = useCallback(
    (connectorOverrides) => {
      return queryIssueFieldsForConnector(connectorId, connectorOverrides);
    },
    [connectorId, queryIssueFieldsForConnector]
  );

  // Show loading state while fetching connector
  if (isConnectorLoading || (!connector && connectorId)) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '200px' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading connector...</span>
        </div>
        <span className="ml-2">Loading connector...</span>
      </div>
    );
  }

  // Show error state if connector not found
  if (!connector) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Connector Not Found</h4>
        <p>The connector you're trying to edit could not be found.</p>
        <hr />
        <button className="btn btn-outline-danger" onClick={handleCancel}>
          Back to Connectors
        </button>
      </div>
    );
  }

  return (
    <ConnectorEdit
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onTest={handleTestConnector}
      connector={connector}
      workspace={activeWorkspace}
      issueFields={issueFields}
      onQueryIssueFields={handleQueryIssueFields}
    />
  );
}

const mapStateToProps = (state, ownProps) => {
  const connectorId = ownProps.match.params.connectorId;
  return {
    activeWorkspace: selectActiveWorkspace(state),
    connector: selectConnectorById(state, connectorId),
  };
};

const mapDispatchToProps = {
  updateConnector,
  testConnectorCredentials,
  fetchConnector,
  notify,
  queryIssueFieldsForConnector,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ConnectorEditContainer)
);
